import {
    Button,
    Input,
    maxRule,
    minRule,
    requiredRule,
    toMoneyString,
    ValidatedInput,
} from '@finbeat/react-toolkit';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Money from 'components/Common/Money';
import { Dictionary } from 'lodash';
import { MerchantOrderProductAdd } from 'models/api/merchant';
import { useMemo, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Accordion, AccordionBody, AccordionHeader, AccordionItem, Col } from 'reactstrap';
import { MerchantOrderModalFormType } from './MerchantOrderModal';
import { numberFromLocaleString } from 'utils/number';

const emptyItem: MerchantOrderProductAdd = {
    productName: '',
    productCost: '0',
    productCnt: null,
};

type MerchantOrderProductsProps = {
    settingsFetching: boolean;
    settings: Dictionary<string> | null;
    isEditable: boolean;
};

type ProductSettings = {
    productsLimit: number;
    productCountLimit: number;
    productCostLimit: number;
};

const productSettingsDefault: ProductSettings = {
    productsLimit: 10,
    productCountLimit: 10,
    productCostLimit: 100000000,
};

const MerchantOrderProducts = ({
    settingsFetching,
    settings,
    isEditable,
}: MerchantOrderProductsProps) => {
    const { t, i18n } = useTranslation();

    const { append, remove, fields } = useFieldArray<MerchantOrderModalFormType>({
        name: 'products',
    });

    const [open, setOpen] = useState('product-0');

    const { watch } = useFormContext<MerchantOrderModalFormType>();

    const accordionProps = {
        open: open,
        toggle: (id: string) => setOpen((prev) => (prev === id ? '' : id)),
    };

    const addProduct = () => {
        append(emptyItem);
        setOpen(`product-${fields.length}`);
    };

    const deleteProduct = (ids: number) => {
        remove(ids);
    };

    const productsSettings: ProductSettings = useMemo(
        () =>
            settings
                ? {
                      productsLimit:
                          (settings['ProductsLimit'] && parseInt(settings['ProductsLimit'])) ||
                          productSettingsDefault.productsLimit,
                      productCountLimit:
                          (settings['ProductCntLimit'] && parseInt(settings['ProductCntLimit'])) ||
                          productSettingsDefault.productCountLimit,
                      productCostLimit:
                          (settings['ProductMaxCost'] && parseInt(settings['ProductMaxCost'])) ||
                          productSettingsDefault.productCostLimit,
                  }
                : productSettingsDefault,
        [settings]
    );

    return (
        <Accordion {...accordionProps}>
            <>
                {fields.map((item, idx) => {
                    const productCnt = watch(`products.${idx}.productCnt`),
                        productCost = watch(`products.${idx}.productCost`),
                        productSum =
                            productCnt && productCost
                                ? productCnt * numberFromLocaleString(productCost, i18n.language)
                                : 0,
                        rawProductSum = toMoneyString(productSum?.toString(), i18n.language);

                    const pNumber = idx + 1;

                    return (
                        <AccordionItem key={`product-${pNumber}`}>
                            <AccordionHeader targetId={`product-${idx}`}>
                                <Col>
                                    <Trans i18nKey="BackOfficeProduct" /> №{pNumber}
                                    {': '}
                                    <Money value={productSum} />
                                </Col>
                                <Button
                                    color="link"
                                    hidden={idx === 0 || !isEditable}
                                    onClick={() => deleteProduct(idx)}
                                >
                                    <Trans i18nKey="BackOfficeDelete" />
                                </Button>
                            </AccordionHeader>
                            <AccordionBody accordionId={`product-${idx}`}>
                                <ValidatedInput<MerchantOrderModalFormType>
                                    id={`mop-product-name-${idx}`}
                                    name={`products.${idx}.productName`}
                                    label={t('BackOfficeProductName')}
                                    rules={{
                                        required: requiredRule,
                                        maxLength: maxRule(255),
                                    }}
                                    disabled={!isEditable}
                                />
                                <ValidatedInput<MerchantOrderModalFormType>
                                    id={`mop-product-cost-${idx}`}
                                    name={`products.${idx}.productCost`}
                                    label={t('BackOfficeProductCost')}
                                    rules={{
                                        required: requiredRule,
                                        validate: (v) => {
                                            const cost = numberFromLocaleString(v, i18n.language);
                                            if (cost > productsSettings.productCostLimit)
                                                return 'BackOfficeMaxError';
                                            if (cost < 1) return 'BackOfficeMinError';
                                        },
                                    }}
                                    numberFormatLocale={i18n.language}
                                    type="text"
                                    disabled={!isEditable}
                                    loading={settingsFetching}
                                />
                                <ValidatedInput<MerchantOrderModalFormType>
                                    id={`mop-product-cnt-${idx}`}
                                    name={`products.${idx}.productCnt`}
                                    label={t('BackOfficeProductCount')}
                                    rules={{
                                        required: requiredRule,
                                        min: minRule(1),
                                        max: maxRule(productsSettings.productCountLimit),
                                    }}
                                    type="number"
                                    disabled={!isEditable}
                                    loading={settingsFetching}
                                />
                                <Input
                                    label={t('BackOfficeProductTotalSum')}
                                    readOnly
                                    disabled={!isEditable}
                                    value={rawProductSum}
                                />
                            </AccordionBody>
                        </AccordionItem>
                    );
                })}
                <Button
                    className="xs-1 p-3"
                    color="link"
                    onClick={addProduct}
                    hidden={!isEditable || fields.length >= productsSettings.productsLimit}
                >
                    <FontAwesomeIcon icon={faPlus} /> <Trans i18nKey="BackOfficeAdd" />
                </Button>
            </>
        </Accordion>
    );
};

export default MerchantOrderProducts;
